'use client';

import { type CSSProperties } from 'react';
import { cn } from '../../lib/cn';

export type SRadioButtonSize = 'xs' | 'sm';

export interface SRadioButtonOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SRadioButtonProps {
  /** 옵션 목록 */
  options?: SRadioButtonOption[];
  /** 현재 선택 값 */
  value?: string | number | null;
  /** 크기 */
  size?: SRadioButtonSize;
  /** 전체 비활성 */
  disabled?: boolean;
  /** input name 속성 */
  name?: string;
  /** 변경 (sdUpdate) */
  onValueChange?: (value: string | number) => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * SRadioButton — sd-radio-button 포팅. 세그먼트형 라디오(버튼 그룹).
 * default(흰 배경/회색, hover 파란 채움) · select(흰 배경/파란 테두리·글자) · disabled.
 */
export function SRadioButton({
  options = [],
  value,
  size = 'sm',
  disabled = false,
  name,
  onValueChange,
  className,
  style,
}: SRadioButtonProps) {
  return (
    <div
      role="radiogroup"
      aria-disabled={disabled}
      className={cn('inline-flex', className)}
      style={style}
    >
      {options.map((o, i) => {
        const selected = o.value === value;
        const od = disabled || o.disabled;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            name={name}
            aria-checked={selected}
            disabled={od}
            onClick={() => !od && !selected && onValueChange?.(o.value)}
            className={cn(
              'relative box-border inline-flex items-center justify-center whitespace-nowrap border border-solid text-[12px] font-medium leading-[20px] transition-colors',
              i === 0 ? 'rounded-l-[var(--cmp-radioButton-radius)]' : '-ml-px',
              i === options.length - 1 ? 'rounded-r-[var(--cmp-radioButton-radius)]' : '',
              selected ? 'z-10' : '',
              !od &&
                !selected &&
                'hover:!bg-[var(--cmp-radioButton-bg-hover)] hover:!text-[color:var(--cmp-radioButton-content-hover)] hover:!border-[color:var(--cmp-radioButton-bg-hover)] hover:z-10',
              od ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            style={{
              height: `var(--cmp-radioButton-${size}-height)`,
              padding: `0 var(--cmp-radioButton-${size}-paddingX)`,
              background: od
                ? 'var(--cmp-radioButton-bg-disabled)'
                : 'var(--cmp-radioButton-bg-default)',
              borderColor: od
                ? 'var(--cmp-radioButton-border-disabled)'
                : selected
                  ? 'var(--cmp-radioButton-border-select)'
                  : 'var(--cmp-radioButton-border-default)',
              color: od
                ? 'var(--cmp-radioButton-content-disabled)'
                : selected
                  ? 'var(--cmp-radioButton-content-select)'
                  : 'var(--cmp-radioButton-content-default)',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
